import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { addSeconds, format, formatDistanceStrict, isAfter } from 'date-fns'
import lang from 'lang'
import { isAuthenticated } from 'services/selectors'
import { submitGroupMoodMeterPlot } from 'page-modules/mood-meter/service/actions'
import GroupTimer from 'page-modules/mood-meter/group-timer'
import PlotJourney from 'page-modules/mood-meter/plot-journey'
import { replace, timeDuration } from 'common/utils'
import { AppState } from 'common/store/reducer'

const connector = connect(
  (state: AppState) => ({
    isAuthenticated: isAuthenticated(state),
    group: state.moodMeter.group.item,
  }),
  (dispatch) => ({
    submitGroupMoodMeterPlot: (groupId?: string) => dispatch(submitGroupMoodMeterPlot(groupId)),
  }),
)

type Props = ConnectedProps<typeof connector>

class InGroupPlot extends React.Component<Props> {
  private groupStartTimeInterval: NodeJS.Timeout | null = null

  componentDidMount() {
    if (!this.hasGroupStarted) {
      this.startCheckingGroupStartTime()
    }
  }

  get hasGroupStarted() {
    if (!this.props.group?.start_time) return false
    return isAfter(new Date(), new Date(this.props.group.start_time))
  }

  get startTimeDate() {
    return new Date(this.props.group?.start_time || new Date())
  }

  get endTimeDate() {
    if (!this.props.group?.duration) return new Date()
    const seconds = timeDuration(this.props.group.duration).asSeconds()
    return addSeconds(this.startTimeDate, seconds)
  }

  startCheckingGroupStartTime() {
    this.groupStartTimeInterval = setInterval(() => {
      if (isAfter(new Date(), this.startTimeDate)) {
        this.stopCheckingGroupStartTime()
        this.forceUpdate()
      }
    }, 1000)
  }

  stopCheckingGroupStartTime() {
    if (this.groupStartTimeInterval) {
      clearInterval(this.groupStartTimeInterval)
      this.groupStartTimeInterval = null
    }
  }

  renderStartedGroupStatus() {
    const collected_moods = this.props.group?.collected_moods || 0

    return (
      <>
        <p className="page-subtitle">
          {collected_moods}{' '}
          {collected_moods === 1
            ? lang.page.moodMeter.personHasPlottedTheirMood
            : lang.page.moodMeter.peopleHavePlottedTheirMood}
          {!this.props.isAuthenticated ? (
            <>
              <br />
              {`${lang.page.moodMeter.plotOpened}: ${format(
                this.startTimeDate,
                'MM-dd-yyyy hh:mm a',
              )}`}
            </>
          ) : null}
        </p>
        <GroupTimer
          duration={this.props.group?.duration || ''}
          closed={!!this.props.group?.closed}
          startTimeDate={this.startTimeDate}
          endTimeDate={this.endTimeDate}
        />
      </>
    )
  }

  renderNotStartedGroupStatus() {
    const formatedStartDate = format(this.startTimeDate, 'MMM dd, yyyy, hh:mm a')
    const formatedDuration = formatDistanceStrict(this.startTimeDate, this.endTimeDate)
    return (
      <p className="page-subtitle">
        {replace(lang.page.moodMeter.notStarted, [formatedStartDate, formatedDuration])}
      </p>
    )
  }

  renderPlotJourney() {
    if (this.props.group?.closed) {
      return (
        <div className="grid-container margin-top-1">
          <div className="grid-x grid-padding-x align-center">
            <div className="cell">
              <h2 className="closed">{lang.page.moodMeter.closed}</h2>
            </div>
          </div>
        </div>
      )
    }
    return (
      <PlotJourney
        withGroupId={this.props.group?.id}
        submitMoodMeterPlot={this.props.submitGroupMoodMeterPlot}
      />
    )
  }

  render() {
    return (
      <>
        <div className="hero-section">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="small-12 medium-12 large-8 cell">
                {!this.props.isAuthenticated ? <h3>{lang.page.moodMeter.plotYourMood}</h3> : null}
                <h1 className="page-title">{this.props.group?.name}</h1>
                {this.hasGroupStarted
                  ? this.renderStartedGroupStatus()
                  : this.renderNotStartedGroupStatus()}
              </div>
            </div>
          </div>
        </div>
        {this.hasGroupStarted ? this.renderPlotJourney() : null}
      </>
    )
  }
}

export default connector(InGroupPlot)
